import { useReducer, useCallback, useEffect, useRef } from 'react';
import type { SearchSettings } from '~/config/search-settings';
import { DEFAULT_SETTINGS, isDefaultSettings } from '~/config/search-settings';
import type { Tld } from '~/config/tld-config';
import type { DomainGenerationMode, CreativityLevel } from '~/types/domain';
import {
  getStoredSettings as getStoredSettingsFromStorage,
  saveSettingsToStorage as saveSettingsToStorageUtil,
} from '~/lib/settings-storage';

type SettingsAction =
  | { type: 'SET_MODE'; payload: DomainGenerationMode }
  | { type: 'SET_MAX_LENGTH'; payload: number }
  | { type: 'SET_SELECTED_TLDS'; payload: Tld[] }
  | { type: 'SET_CREATIVITY_LEVEL'; payload: CreativityLevel }
  | { type: 'SET_TLD_MODE'; payload: 'auto' | 'explicit' }
  | { type: 'SET_DOMAIN_LANGUAGE'; payload: string }
  | { type: 'SET_AVOID_EXPENSIVE_TLDS'; payload: boolean }
  | { type: 'LOAD_SETTINGS'; payload: Partial<SearchSettings> }
  | { type: 'RESET_TO_DEFAULTS' };

function settingsReducer(
  state: SearchSettings,
  action: SettingsAction
): SearchSettings {
  switch (action.type) {
    case 'SET_MODE':
      return state.mode === action.payload
        ? state
        : { ...state, mode: action.payload };
    case 'SET_MAX_LENGTH':
      return state.maxLength === action.payload
        ? state
        : { ...state, maxLength: action.payload };
    case 'SET_SELECTED_TLDS':
      return state.selectedTlds === action.payload
        ? state
        : { ...state, selectedTlds: action.payload };
    case 'SET_CREATIVITY_LEVEL':
      return state.creativityLevel === action.payload
        ? state
        : { ...state, creativityLevel: action.payload };
    case 'SET_TLD_MODE':
      return state.tldMode === action.payload
        ? state
        : { ...state, tldMode: action.payload };
    case 'SET_DOMAIN_LANGUAGE':
      return state.domainLanguage === action.payload
        ? state
        : { ...state, domainLanguage: action.payload };
    case 'SET_AVOID_EXPENSIVE_TLDS':
      return state.avoidExpensiveTlds === action.payload
        ? state
        : { ...state, avoidExpensiveTlds: action.payload };
    case 'LOAD_SETTINGS': {
      let hasChanged = false;
      for (const key in action.payload) {
        if (
          Object.prototype.hasOwnProperty.call(action.payload, key) &&
          action.payload[key as keyof SearchSettings] !==
            state[key as keyof SearchSettings]
        ) {
          if (
            key === 'selectedTlds' &&
            Array.isArray(action.payload.selectedTlds) &&
            Array.isArray(state.selectedTlds)
          ) {
            if (
              JSON.stringify(action.payload.selectedTlds.sort()) !==
              JSON.stringify(state.selectedTlds.sort())
            ) {
              hasChanged = true;
              break;
            }
          } else {
            hasChanged = true;
            break;
          }
        }
      }
      return hasChanged ? { ...state, ...action.payload } : state;
    }
    case 'RESET_TO_DEFAULTS':
      return JSON.stringify(state) === JSON.stringify(DEFAULT_SETTINGS)
        ? state
        : { ...DEFAULT_SETTINGS };
    default:
      return state;
  }
}

export function useSearchSettings(/* query: string */) {
  const [settings, dispatch] = useReducer(settingsReducer, DEFAULT_SETTINGS);
  const isMounted = useRef(true);

  // Track mount status
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Computed value for whether settings have been customized
  const hasCustomSettings = !isDefaultSettings(settings);

  // Load settings from localStorage on initial render
  useEffect(() => {
    try {
      const savedSettings = getStoredSettingsFromStorage();
      if (savedSettings && isMounted.current) {
        dispatch({ type: 'LOAD_SETTINGS', payload: savedSettings });
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
  }, []);

  // Save settings to localStorage
  const saveSettingsToStorage = useCallback(
    (currentQuery: string) => {
      if (!isMounted.current) return;
      saveSettingsToStorageUtil(settings, currentQuery);
    },
    [settings]
  );

  return {
    settings,
    dispatch,
    hasCustomSettings,
    saveSettingsToStorage,

    // Action creators
    setMode: (mode: DomainGenerationMode) =>
      dispatch({ type: 'SET_MODE', payload: mode }),
    setMaxLength: (length: number) =>
      dispatch({ type: 'SET_MAX_LENGTH', payload: length }),
    setSelectedTlds: (tlds: Tld[]) =>
      dispatch({ type: 'SET_SELECTED_TLDS', payload: tlds }),
    setCreativityLevel: (level: CreativityLevel) =>
      dispatch({ type: 'SET_CREATIVITY_LEVEL', payload: level }),
    setTldMode: (mode: 'auto' | 'explicit') =>
      dispatch({ type: 'SET_TLD_MODE', payload: mode }),
    setDomainLanguage: (lang: string) =>
      dispatch({ type: 'SET_DOMAIN_LANGUAGE', payload: lang }),
    setAvoidExpensiveTlds: (avoid: boolean) =>
      dispatch({ type: 'SET_AVOID_EXPENSIVE_TLDS', payload: avoid }),
    resetToDefaults: () => dispatch({ type: 'RESET_TO_DEFAULTS' }),
  };
}
